export const en = {
  announcement: {
    waitlistCta: "Join FONDEA",
    waitlistCtaShort: "FONDEA",
    message: "Companies evolve. The way they operate must evolve too.",
    cta: "Watch video",
    openVideo: "Play announcement video",
    dismiss: "Hide announcement",
    modalTitle: "IBYLIT announcement video",
  },
  a11y: {
    skipToContent: "Skip to main content",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    toggleTheme: "Toggle theme",
    toggleLanguage: "Toggle language",
    mainNavigation: "Main navigation",
    footerNavigation: "Footer navigation",
    accessibilityStatement:
      "This website is designed to align with WCAG 2.2 Level AA accessibility standards and is continuously improved.",
  },
  nav: {
    solutions: "Solutions",
    services: "Services",
    solutionItems: {
      overview: "Overview",
      banks: "Banks and financial institutions",
      corporations: "Corporations",
      fintechs: "Fintechs and operators",
      funds: "Funds and funders",
    },
    serviceItems: {
      overview: "Overview",
      advisory: "Advisory and consulting",
      implementation: "Implementation",
      turnkey: "Integrations and turnkey projects",
      cloud: "Cloud Managed Services",
      support: "Maintenance, support and evolution",
      academy: "IBYLIT Academy",
    },
    platform: "Platform",
    products: "Products",
    infrastructure: "Infrastructure",
    ecosystem: "Ecosystem",
    security: "Security",
    resources: "Resources",
    company: "Company",
    requestDemo: "Request Demo",
    platformItems: {
      overview: "Overview",
      financialOperations: "Financial Operations",
      liquidityManagement: "Liquidity Management",
      documentProcessing: "Document Processing",
      calculationEngine: "Calculation Engine",
      accountingAutomation: "Accounting Automation",
      integrations: "Integrations",
    },
    productItems: {
      digitalFactoring: "Digital Factoring",
      reverseFactoring: "Reverse Factoring",
      b2bPayments: "B2B Payments",
      invoiceAuctions: "Invoice Auctions",
    },
    infrastructureItems: {
      architecture: "Architecture",
      cloudPlatform: "Cloud Platform",
      apis: "APIs",
      whiteLabel: "SOLVENN",
      scalability: "Scalability",
    },
    ecosystemItems: {
      companies: "Companies",
      funders: "Funders",
      banks: "Banks",
      corporations: "Corporations",
    },
    securityItems: {
      compliance: "Compliance",
      securityArchitecture: "Security Architecture",
      audit: "Audit & Traceability",
    },
    resourceItems: {
      blog: "Blog",
      documentation: "Documentation",
      caseStudies: "Case Studies",
    },
    companyItems: {
      about: "About IBYLIT",
      mission: "Mission",
      vision: "Vision",
      contact: "Contact",
    },
  },
  hero: {
    badge: "B2B Fintech Platform - Cloud",
    title: "The infrastructure that connects banks, companies and funders",
    titleHighlight: "to structure liquidity on real assets",
    subtitle: "IBYLIT is the cloud orchestrator that brings ERPs, banks, corporates and funders into a single flow. We don't finance, we don't execute payments: we structure the ecosystem so liquidity flows.",
    cta1: "Explore Platform",
    cta2: "Request Demo",
    nodes: { companies: "Companies", banks: "Banks", funders: "Funders", erps: "ERPs" },
  },
  problem: {
    badge: "The Problem",
    title: "Business liquidity is broken",
    subtitle: "Companies face cash flow gaps while financial institutions seek reliable commercial assets to finance — but traditional processes are too slow and manual.",
    items: [
      { title: "Slow Payment Cycles", description: "Companies wait 30–90 days for invoice payments, creating cash flow gaps that limit growth and operations." },
      { title: "Manual Processes", description: "Traditional financing relies on paper-based workflows, manual approvals, and fragmented communication." },
      { title: "Disconnected Systems", description: "Financial institutions lack a unified platform to connect companies, funders, ERPs and tax authorities." },
    ],
  },
  solution: {
    badge: "The Solution",
    title: "Infrastructure that structures the liquidity flow",
    subtitle: "IBYLIT structures the flow between those who generate assets and those who provide capital, in a single white-label platform.",
    bullets: [
      "Unified platform for all participants",
      "White-label deployment under your brand",
      "Core-agnostic integration with your existing stack",
      "Modular, embeddable, API-oriented",
      "Real-time settlement traceability",
      "Enterprise-grade security & compliance",
    ],
    steps: [
      "Companies register their assets",
      "IBYLIT validates and structures",
      "Funders allocate capital",
      "End-to-end traceable settlement",
    ],
  },
  howItWorks: {
    badge: "How It Works",
    title: "Four steps to digital liquidity",
    subtitle: "End-to-end automation from invoice upload to capital delivery.",
    step: "STEP",
    steps: [
      { title: "Upload", description: "Companies register assets on the platform." },
      { title: "Validate", description: "Assets are verified against ERPs and official sources." },
      { title: "Evaluate", description: "Funders evaluate opportunities in real-time." },
      { title: "Liquidity", description: "Funders execute; IBYLIT records and traces the cycle." },
    ],
  },
  products: {
    badge: "Products",
    title: "Complete liquidity product suite",
    subtitle: "Deploy best-in-class financial products from a single platform.",
    items: [
      { title: "Digital Factoring", description: "Structures receivables discounting by connecting companies with verified funders." },
      { title: "Reverse Factoring", description: "Supply chain programs under your brand with point-to-point traceability." },
      { title: "B2B Payments", description: "Orchestrates and traces payment flows between businesses; banking rails handle execution." },
      { title: "Invoice Auctions", description: "Competitive, verifiable marketplaces where multiple funders allocate capital." },
    ],
  },
  platformCaps: {
    badge: "Platform",
    title: "Built for financial operations at scale",
    subtitle: "Core capabilities designed for enterprise-grade financial workflows.",
    items: [
      { title: "Financial Orchestration", description: "Coordinates the instrument lifecycle from origination to settlement, without executing." },
      { title: "Document Processing", description: "Automated validation of invoices, contracts and financial documents." },
      { title: "Calculation Engine", description: "Real-time computation of rates, interest and fees." },
      { title: "Accounting Reconciliation", description: "Generates entries and reconciliations for external accounting systems." },
      { title: "Integration Engine", description: "Ready connectors for ERPs, banking cores and accounting systems." },
      { title: "Notifications & Alerts", description: "Real-time events on instrument status and approvals." },
    ],
  },
  infrastructure: {
    badge: "Infrastructure",
    title: "Enterprise-grade cloud infrastructure",
    subtitle: "Built for scalability, security, and reliability from the ground up.",
    items: [
      { title: "Cloud-Native", description: "Built on AWS with auto-scaling, high availability, and global deployment." },
      { title: "API-First", description: "RESTful APIs with comprehensive documentation for seamless integration." },
      { title: "Modular Architecture", description: "Microservices-based design allowing independent scaling and deployment." },
      { title: "Secure by Design", description: "End-to-end encryption, SOC2 principles, and ISO 27001 alignment." },
      { title: "High Performance", description: "Sub-second transaction processing with 99.9% uptime SLA." },
      { title: "Core Adaptable", description: "Non-invasive integration with existing banking cores — IBYLIT adapts to your infrastructure, not the other way around." },
    ],
  },
  integrations: {
    badge: "Integrations",
    title: "Connect your entire financial stack",
    subtitle: "Pre-built connectors for the systems your business already uses.",
    items: [
      { title: "ERPs", description: "Bi-directional sync with enterprise resource planning systems.", tags: ["SAP", "Oracle", "NetSuite", "Custom ERPs"] },
      { title: "Banking Cores", description: "Direct connectivity to banking infrastructure.", tags: ["Core Banking", "Payment Rails", "SWIFT", "ACH"] },
      { title: "Tax Authorities", description: "Real-time validation against government systems.", tags: ["Electronic Invoicing", "Tax Validation", "Fiscal Compliance"] },
      { title: "Accounting", description: "Automated journal entries and reconciliation.", tags: ["QuickBooks", "Xero", "Custom GL"] },
    ],
  },
  ecosystem: {
    badge: "Ecosystem",
    title: "A connected financial ecosystem",
    subtitle: "IBYLIT connects all participants in the liquidity chain through a single platform.",
    chain: ["Companies", "Assets", "IBYLIT", "Funders", "Liquidity"],
    participants: [
      { title: "Banks", description: "Launch factoring, confirming and auctions under their brand, integrated to their core, without replacing infrastructure." },
      { title: "Corporates", description: "Structure supplier payment programs and optimize working capital with point-to-point traceability." },
      { title: "SMEs", description: "Access liquidity on their invoices through the connected funder network, with no operational friction." },
      { title: "Funders", description: "Structured access to verified opportunities, with operational custody, auctions and traceable settlement.", bullets: ["Structured access to opportunities", "Operational custody of instruments", "Verifiable auctions", "Settlement with traceability"], disclaimer: "Registry is internal — does not constitute regulated custody." },
    ],
  },
  annotation: {
    badge: "Annotation & Operational Custody",
    title: "Instrument lifecycle, registered and traced",
    subtitle: "IBYLIT registers each financial instrument from origination to settlement with full operational traceability.",
    items: [
      { title: "Account Annotation", description: "Digital registration of the instrument with unique identifier and operational metadata." },
      { title: "Operational Custody", description: "Logical safekeeping of the instrument's state throughout its lifecycle." },
      { title: "Traceable Lifecycle", description: "Origination, assignment, funding and settlement events are recorded immutably." },
    ],
    disclaimer: "IBYLIT's annotation and operational custody are internal platform functions and do not constitute custody regulated by an authorized entity.",
  },
  securitySection: {
    badge: "Security",
    title: "Security & trust",
    subtitle: "Security-first architecture protecting every financial operation.",
    items: [
      { title: "TLS 1.2 / 1.3 encryption", description: "All traffic is delivered over HTTPS with end-to-end encryption." },
      { title: "AWS infrastructure", description: "Global deployment on AWS with CloudFront distribution and per-region isolation." },
      { title: "Anti-bot protection", description: "Cloudflare Turnstile validates every critical interaction to prevent automated abuse." },
      { title: "Advanced rate limiting", description: "Per-IP, per-device and per-session limits that mitigate brute force and abuse." },
      { title: "Continuous monitoring", description: "24/7 telemetry, alerting and immutable audit trails across the platform." },
      { title: "SOC 2 principles", description: "Operational controls aligned with SOC 2 Type II and ISO 27001." },
    ],
    scene: {
      people: "People & access",
      perimeter: "Perimeter controls",
      infra: "Infrastructure & code",
      monitor: "Live telemetry",
    },
  },
  cta: {
    title: "Transform your financial infrastructure",
    subtitle: "Request a demo to explore how IBYLIT enables digital liquidity for banks, fintechs, and corporations.",
    cta1: "Request Demo",
    cta2: "Explore Platform",
  },
  footer: {
    tagline: "B2B financial infrastructure orchestrator.",
    eyebrow: "FINANCIAL INFRASTRUCTURE",
    description: "Ibylit connects companies, banks, ERPs and funders to operate digital liquidity solutions under a unified architecture.",
    ctaDemo: "Request Demo",
    follow: "Follow us",
    badges: ["CLOUD-NATIVE", "API-FIRST", "MODULAR", "SCALABLE"],
    copyright: "All rights reserved.",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    categories: {
      Platform: "Platform",
      Products: "Products",
      Infrastructure: "Infrastructure",
      Security: "Security",
      Company: "Company",
    },
    links: {
      overview: "Overview",
      financialOperations: "Financial Operations",
      integrations: "Integrations",
      digitalFactoring: "Digital Factoring",
      reverseFactoring: "Reverse Factoring",
      b2bPayments: "B2B Payments",
      architecture: "Architecture",
      cloudPlatform: "Cloud Platform",
      apis: "APIs",
      compliance: "Compliance",
      audit: "Audit & Traceability",
      about: "About",
      contact: "Contact",
    },
    columns: {
      products: {
        title: "Products",
        items: [
          { name: "Digital Factoring", desc: "Immediate liquidity for receivables", href: "/products/digital-factoring" },
          { name: "Reverse Factoring", desc: "Supply chain financing", href: "/products/reverse-factoring" },
          { name: "B2B Payments", desc: "Real-time enterprise payments", href: "/products/b2b-payments" },
          { name: "Instrument Auctions", desc: "Competitive funding marketplaces", href: "/products/invoice-auctions" },
          { name: "Account Annotation", desc: "Electronic instrument registry", href: "/products/annotation" },
          { name: "Operational Custody", desc: "Instrument management & control", href: "/products/custody" },
        ],
      },
      platform: {
        title: "Platform",
        items: [
          { name: "Overview", href: "/platform/overview" },
          { name: "Financial Operations", href: "/platform/financial-operations" },
          { name: "Integrations", href: "/platform/integrations" },
          { name: "Use Cases", href: "/platform/use-cases" },
          { name: "Resources", href: "/platform/resources" },
          { name: "Documentation", href: "/platform/docs" },
          { name: "Regulatory framework", href: "/platform/regulatory-framework" },
        ],
      },
      infrastructure: {
        title: "Infrastructure",
        items: [
          { name: "Architecture", href: "/infrastructure/architecture" },
          { name: "Cloud Platform", href: "/infrastructure/cloud-platform" },
          { name: "APIs", href: "/infrastructure/apis" },
          { name: "Availability", href: "/infrastructure/availability" },
          { name: "Performance", href: "/infrastructure/performance" },
        ],
      },
      security: {
        title: "Security",
        items: [
          { name: "Compliance", href: "/security/compliance" },
          { name: "Data Security", href: "/security/data" },
          { name: "Privacy", href: "/security/privacy" },
          { name: "Policies", href: "/security/policies" },
        ],
      },
      company: {
        title: "Company",
        items: [
          { name: "About Ibylit", href: "/company/about" },
          { name: "Our Team", href: "/company/team" },
          { name: "Contact", href: "/company/contact" },
          { name: "Partners", href: "/company/partners" },
          { name: "Careers", href: "/company/careers" },
          { name: "Privacy Policy", href: "/legal/privacy" },
          { name: "Cookie Policy", href: "/legal/cookies" },
          { name: "Terms of Use", href: "/legal/terms" },
        ],
      },
    },
  },
  subpage: {
    notFound: "Page Not Found",
    backHome: "Back to Home",
    requestDemo: "Request Demo",
  },
  pages: {
    platform: {
      overview: {
        title: "Platform Overview",
        subtitle: "A complete financial infrastructure platform for digital liquidity operations.",
        content: [
          "IBYLIT is a cloud-native B2B white-label financial infrastructure platform that enables banks, fintechs, and corporations to operate digital liquidity solutions.",
          "The platform provides end-to-end financial operations management, from invoice processing to settlement, all under your own brand.",
          "With modular architecture and comprehensive APIs, IBYLIT integrates seamlessly into your existing technology stack.",
        ],
      },
      "financial-operations": {
        title: "Financial Operations",
        subtitle: "Manage the entire lifecycle of financial transactions.",
        content: [
          "From origination to settlement, IBYLIT automates every step of the financial operations workflow.",
          "Real-time transaction tracking, automated approvals, and comprehensive reporting.",
          "Multi-currency support with configurable business rules and compliance checks.",
        ],
      },
      "liquidity-management": {
        title: "Liquidity Management",
        subtitle: "Optimize working capital and cash flow across your organization.",
        content: [
          "Real-time visibility into cash positions, receivables, and payables.",
          "Automated cash flow forecasting with scenario modeling capabilities.",
          "Integration with treasury management systems for unified liquidity views.",
        ],
      },
      "document-processing": {
        title: "Document Processing",
        subtitle: "Automated validation and processing of financial documents.",
        content: [
          "AI-powered invoice validation against tax authority databases.",
          "Automated document classification, extraction, and verification.",
          "Support for electronic invoicing standards across multiple jurisdictions.",
        ],
      },
      "calculation-engine": {
        title: "Financial Calculation Engine",
        subtitle: "Real-time financial calculations for all liquidity operations.",
        content: [
          "Configurable discount rate calculations with multiple pricing models.",
          "Real-time interest computation, fee calculations, and spread analysis.",
          "Support for complex financial structures including tranched financing.",
        ],
      },
      "accounting-automation": {
        title: "Accounting Automation",
        subtitle: "Automated journal entries and financial reconciliation.",
        content: [
          "Automated generation of journal entries for all financial transactions.",
          "Real-time reconciliation between platform transactions and accounting records.",
          "Integration with major accounting systems and general ledgers.",
        ],
      },
      integrations: {
        title: "Integrations",
        subtitle: "Connect your entire financial technology stack.",
        content: [
          "Pre-built connectors for SAP, Oracle, NetSuite, and other major ERPs.",
          "Banking core integrations for payment rails, SWIFT, and ACH.",
          "Tax authority APIs for real-time invoice validation and fiscal compliance.",
        ],
      },
      "use-cases": {
        title: "Use Cases",
        subtitle: "Banks launch white-label factoring, corporates deploy confirming, funders access verified portfolios.",
        content: [
          "Banks use IBYLIT to launch factoring and confirming products under their own brand without rebuilding their core.",
          "Corporates deploy supply-chain finance programs that strengthen supplier relationships while optimizing working capital.",
          "Funders gain structured access to verified instrument portfolios with operational custody and traceable settlement.",
          "Each use case is delivered as a configurable workflow on top of the same orchestration platform — not as a separate product.",
        ],
      },
      resources: {
        title: "Resources",
        subtitle: "Materials for solution architects, product teams and financial stakeholders.",
        content: [
          "Our resource center brings together solution briefs, architecture overviews and operational playbooks for teams evaluating or deploying IBYLIT.",
          "Materials are organized by audience: technical architects, product owners and treasury / risk stakeholders.",
          "Detailed materials are shared during structured evaluation engagements — request access at soporte@ibylit.com.",
        ],
      },
      docs: {
        title: "Documentation",
        subtitle: "Technical guides, API references and integration examples — developer portal coming soon.",
        content: [
          "IBYLIT provides technical documentation covering platform concepts, REST API references, webhook events and integration patterns.",
          "Reference implementations and example payloads are maintained for the most common integration scenarios.",
          "A self-serve developer portal is on the roadmap; in the meantime, documentation is delivered to integration teams during onboarding.",
        ],
      },
    },
    products: {
      "digital-factoring": {
        title: "Digital Factoring",
        subtitle: "Convert receivables into immediate liquidity through automated invoice financing.",
        content: [
          "Digital Factoring allows companies to sell their receivables at a discount to access immediate cash flow.",
          "The platform automates the entire process: invoice upload, validation, funder matching, and settlement.",
          "Benefits for companies: immediate liquidity, no debt on balance sheet, and predictable cash flow.",
          "Benefits for funders: access to verified commercial assets with transparent risk assessment.",
        ],
      },
      "reverse-factoring": {
        title: "Reverse Factoring / Confirming",
        subtitle: "Enable supply chain finance programs under your brand.",
        content: [
          "Reverse Factoring (Confirming) allows large corporations to help their suppliers access early payments.",
          "The buying company confirms invoices, enabling suppliers to receive financing at better rates.",
          "Strengthens supply chain relationships while optimizing working capital for all parties.",
          "White-label deployment allows banks to offer confirming programs to their corporate clients.",
        ],
      },
      "b2b-payments": {
        title: "B2B Payments",
        subtitle: "Automate business-to-business payment flows with real-time settlement.",
        content: [
          "Streamlined B2B payment processing with automated reconciliation and reporting.",
          "Support for multiple payment methods, currencies, and settlement timeframes.",
          "Integration with banking payment rails for efficient fund transfers.",
        ],
      },
      "invoice-auctions": {
        title: "Invoice Auctions",
        subtitle: "Create competitive marketplaces for invoice financing.",
        content: [
          "Enable multiple funders to compete for invoice financing opportunities.",
          "Transparent auction mechanisms that optimize pricing for companies.",
          "Real-time bidding with automated execution and settlement.",
        ],
      },
      annotation: {
        title: "Account Annotation",
        subtitle: "Structured electronic record of financial instruments with unique identifier and operational metadata.",
        content: [
          "Account Annotation provides a structured digital record of every financial instrument managed on the platform — invoices, payment orders, assignment agreements.",
          "Each instrument receives a persistent unique identifier, enriched operational metadata (issuer, debtor, amount, maturity, status) and full event traceability.",
          "This allows banks, funders and corporates to operate with a consistent, auditable instrument ledger consultable via API in real time.",
          "Regulatory disclaimer: Account Annotation is an internal platform record — it does not constitute regulated securities custody nor replace entities authorized by local regulators.",
        ],
      },
      custody: {
        title: "Operational Custody",
        subtitle: "Logical safekeeping of instrument state, position control and end-to-end operational traceability.",
        content: [
          "Operational Custody manages the logical lifecycle of each financial instrument: current state, active holder, assignment events, position changes and settlement.",
          "It allows funders and operators to control consolidated positions, reconcile movements and export operational evidence for internal reporting or audit.",
          "Every operation is recorded with immutable timestamp, actor identity and business context — enabling complete auditability.",
          "Regulatory disclaimer: Operational Custody is a technological logical-safekeeping service — it does not constitute regulated securities custody. Regulated custody remains with entities authorized by the corresponding regulator.",
        ],
      },
    },
    infrastructure: {
      architecture: {
        title: "Architecture",
        subtitle: "Cloud-native microservices architecture built for financial operations.",
        content: [
          "Microservices-based architecture with independent scaling and deployment capabilities.",
          "Event-driven design for real-time transaction processing and notifications.",
          "Built on AWS with multi-region deployment for high availability.",
        ],
      },
      "cloud-platform": {
        title: "Cloud Platform",
        subtitle: "Enterprise-grade cloud infrastructure on AWS.",
        content: [
          "Fully managed cloud platform with auto-scaling and 99.9% uptime SLA.",
          "Global CDN distribution for low-latency access worldwide.",
          "Automated backups, disaster recovery, and infrastructure monitoring.",
        ],
      },
      apis: {
        title: "APIs",
        subtitle: "Comprehensive RESTful APIs for seamless integration.",
        content: [
          "Connect in minutes via standard REST APIs and webhooks. No SDKs, no complex configurations.",
          "100% cloud architecture: zero installations, zero third-party library maintenance.",
          "Sandbox environment + interactive documentation for immediate testing.",
        ],
      },
      "white-label": {
        title: "SOLVENN",
        subtitle: "Deploy under your own brand with complete customization. Adaptable to your existing core.",
        content: [
          "Full brand customization including logos, colors, domains, and email templates.",
          "Isolated multi-tenant environments with dedicated resources per client.",
          "Custom workflows and business rules per deployment.",
          "Core-agnostic architecture: IBYLIT integrates non-invasively with your existing banking core, ERP, or financial system — no need to replace what already works.",
          "Your clients see your brand. Your team controls the experience. IBYLIT powers the infrastructure behind the scenes.",
        ],
      },
      scalability: {
        title: "Scalability",
        subtitle: "Built to grow with your business from day one.",
        content: [
          "Horizontal scaling with containerized microservices.",
          "Database sharding and read replicas for high-throughput operations.",
          "Performance optimization for millions of transactions per day.",
        ],
      },
      availability: {
        title: "Availability",
        subtitle: "99.9% SLA, multi-region AWS deployment, disaster recovery and continuous monitoring.",
        content: [
          "IBYLIT operates with a 99.9% uptime SLA across production environments.",
          "Workloads are deployed across multiple AWS regions with automated failover, encrypted backups and tested disaster recovery procedures.",
          "Platform health is monitored continuously, with on-call coverage and proactive alerting on latency, error rate and saturation signals.",
        ],
      },
      performance: {
        title: "Performance",
        subtitle: "Sub-second processing, horizontal scaling and optimization for millions of daily transactions.",
        content: [
          "Critical operational paths — instrument creation, validation, auction matching and settlement — are designed to respond in sub-second latency.",
          "The platform scales horizontally as transaction volume grows, without architectural redesign.",
          "Performance is benchmarked against representative workloads of millions of instruments per day, ensuring headroom for enterprise-scale operators.",
        ],
      },
      roadmap: {
        title: "Roadmap",
        subtitle: "Upcoming capabilities: new payment rails, additional ERP integrations and vertical market expansion.",
        content: [
          "Our roadmap is structured around three axes: connectivity (new payment rails and ERP/core integrations), product depth (additional financial instruments and workflows) and geography (expansion across LATAM and selected EMEA markets).",
          "Specific capabilities are sequenced together with anchor clients, prioritizing what unlocks measurable operational value.",
          "Detailed roadmap discussions are available under NDA to qualified prospects and partners.",
        ],
      },
    },
    ecosystem: {
      companies: {
        title: "For Companies",
        subtitle: "Access immediate liquidity by converting your receivables into cash.",
        content: [
          "Upload invoices digitally and access financing within hours.",
          "Transparent pricing with competitive rates from multiple funders.",
          "Dashboard for tracking all transactions, payments, and cash flow.",
        ],
      },
      funders: {
        title: "For Funders",
        subtitle: "Access verified commercial assets with transparent risk assessment.",
        content: [
          "Curated portfolio of verified invoices from creditworthy companies.",
          "Real-time risk assessment tools and portfolio management.",
          "Automated settlement and comprehensive reporting.",
        ],
      },
      banks: {
        title: "For Banks",
        subtitle: "Deploy white-label factoring and confirming programs on top of your existing core.",
        content: [
          "Launch digital factoring services under your own brand.",
          "Integrate with your existing banking core and compliance systems — IBYLIT adapts to your infrastructure.",
          "Expand your product offering without building infrastructure from scratch.",
          "No core replacement needed: IBYLIT sits on top of your existing systems as a product layer.",
        ],
      },
      corporations: {
        title: "For Corporations",
        subtitle: "Launch supply chain finance programs to strengthen vendor relationships.",
        content: [
          "Implement confirming programs that benefit your entire supply chain.",
          "Improve supplier payment terms while optimizing your own working capital.",
          "Digital onboarding and management of supplier networks.",
        ],
      },
    },
    security: {
      compliance: {
        title: "Compliance",
        subtitle: "Built with regulatory compliance at its core.",
        content: [
          "SOC 2 Type II principles embedded in platform design and operations.",
          "ISO 27001 alignment for information security management.",
          "Configurable compliance rules for different jurisdictions and regulations.",
        ],
      },
      architecture: {
        title: "Security Architecture",
        subtitle: "Multi-layered security for financial operations.",
        content: [
          "End-to-end encryption for all data in transit and at rest (SSL A+ rating).",
          "Multi-factor authentication and role-based access control.",
          "Network segmentation and intrusion detection systems.",
        ],
      },
      audit: {
        title: "Audit & Traceability",
        subtitle: "Complete transaction traceability with immutable audit logs.",
        content: [
          "Every action is logged with timestamp, user identity, and context.",
          "Immutable audit trails for regulatory compliance and dispute resolution.",
          "Exportable reports for internal and external audit requirements.",
        ],
      },
      data: {
        title: "Data Security",
        subtitle: "AES-256 encryption at rest, TLS 1.3 in transit, key management with HSM.",
        content: [
          "All data managed by IBYLIT is encrypted at rest using AES-256 and in transit using TLS 1.3.",
          "Cryptographic keys are managed in dedicated Hardware Security Modules (HSM) with strict separation of duties.",
          "Access to production data is governed by least-privilege policies, multi-factor authentication and full audit logging.",
        ],
      },
      privacy: {
        title: "Privacy",
        subtitle: "Data handling aligned with GDPR and LGPD, configurable residency and right-to-erasure support.",
        content: [
          "IBYLIT operates under privacy frameworks aligned with GDPR (EU) and LGPD (Brazil), with controls applicable across jurisdictions.",
          "Data residency is configurable per client deployment, allowing operators to host data in the region required by their regulator or business.",
          "Subject rights — access, rectification, erasure and portability — are supported through documented operational procedures.",
        ],
      },
      certifications: {
        title: "Certifications",
        subtitle: "SOC 2 Type II alignment, ISO 27001 alignment, periodic penetration testing.",
        content: [
          "IBYLIT operations are designed in alignment with SOC 2 Type II and ISO 27001 control frameworks.",
          "Independent penetration tests are conducted on a recurring cadence, with remediation tracked through formal vulnerability management.",
          "Compliance evidence and security questionnaires are made available to enterprise clients under NDA during procurement.",
        ],
      },
      policies: {
        title: "Policies",
        subtitle: "Responsible disclosure, incident management and data retention policies.",
        content: [
          "We operate a responsible disclosure program for security researchers — reports can be sent to soporte@ibylit.com.",
          "Security incident response follows a documented playbook covering detection, containment, communication and post-mortem review.",
          "Data retention and deletion timelines are defined per data category and aligned with client contractual terms and regulatory obligations.",
        ],
      },
    },
    resources: {
      blog: {
        title: "Blog",
        subtitle: "Insights on financial technology, liquidity, and digital infrastructure.",
        content: ["Coming soon — stay tuned for articles on fintech infrastructure, liquidity management, and industry trends."],
      },
      documentation: {
        title: "Documentation",
        subtitle: "Technical documentation for developers and integrators.",
        content: ["API documentation, integration guides, and technical references will be available soon."],
      },
      "case-studies": {
        title: "Case Studies",
        subtitle: "See how organizations are using IBYLIT to transform their financial operations.",
        content: ["Case studies showcasing real-world implementations will be published soon."],
      },
    },
    company: {
      about: {
        title: "About IBYLIT",
        subtitle: "We build the infrastructure that connects businesses and capital.",
        content: [
          "IBYLIT is a Costa Rican financial technology infrastructure company. It operates two complementary lines: FONDEA, which brings new liquidity alternatives to businesses, and SOLVENN, which enables financial institutions to modernize their operations under their own brand, with a regional vision for Latin America.",
          "IBYLIT builds technology that enables banks, financial institutions, fintechs and corporations to offer digital working capital solutions under their own brand.",
          "Our platform connects companies, payers and funders in a single environment to manage processes such as factoring, confirming, supplier payments, invoice auctions and other liquidity products. From onboarding participants and validating documents to operations and monitoring, we help make every flow faster, more traceable and more accessible.",
          "We do not fund the operations. We provide the infrastructure that allows institutions and companies to organize their processes, connect their participants and operate liquidity solutions under their own rules.",
          "We work with organizations seeking to modernize their financial services, expand their digital capabilities and better serve companies' working capital needs, without building every solution from scratch.",
          "Our mission: democratize access to business liquidity through technology that connects companies, financial institutions and funding sources.",
          "What we make possible:",
          "Operate under your own brand: white-label solutions aligned with each organization's identity and processes.",
          "Connect participants: companies, payers and funders in a shared digital environment.",
          "Manage more than a transaction: workflows for factoring, confirming, B2B payments and other working capital solutions.",
          "Integrate with the existing ecosystem: technology designed to coexist with each institution's systems and rules.",
          "Improve traceability: information and monitoring of operations throughout their lifecycle.",
          "IBYLIT and FONDEA: IBYLIT provides technology infrastructure so other organizations can operate financial solutions under their own brand. FONDEA, in turn, connects companies with liquidity and financing alternatives within a digital ecosystem.",
          "IBYLIT builds the infrastructure. FONDEA brings it to companies.",
        ],
      },
      mission: {
        title: "Our Mission",
        subtitle: "Democratize access to business liquidity through technology.",
        content: [
          "We believe every company deserves access to efficient, transparent, and affordable liquidity solutions.",
          "By building open financial infrastructure, we enable institutions to serve their clients better and faster.",
        ],
      },
      vision: {
        title: "Our Vision",
        subtitle: "A world where business liquidity flows as freely as information.",
        content: [
          "We envision a future where invoices are instantly convertible to capital, supply chains are financially resilient, and every business has access to the liquidity it needs to grow.",
        ],
      },
      contact: {
        title: "Contact Us",
        subtitle: "Get in touch to learn more about IBYLIT.",
        content: [
          "Email: soporte@ibylit.com",
          "Request a demo to see the platform in action and discuss how IBYLIT can serve your organization.",
        ],
      },
      team: {
        title: "Our Team",
        subtitle: "Leadership with deep experience in banking, fintech and cloud-native enterprise technology.",
        content: [
          "IBYLIT is built by a team that has spent decades operating at the intersection of financial services and enterprise software.",
          "Our leadership combines hands-on experience launching banking products, scaling fintech platforms and architecting cloud-native systems for regulated industries.",
          "We bring this expertise to every deployment — partnering with our clients as long-term infrastructure providers, not as a one-time vendor.",
        ],
      },
      partners: {
        title: "Partners",
        subtitle: "A program for integrators, treasury consultancies and banking technology providers.",
        content: [
          "The IBYLIT Partner Program enables system integrators, treasury advisory firms and core banking technology providers to deliver liquidity solutions to their clients.",
          "Partners receive technical enablement, joint go-to-market support and access to a structured deployment playbook.",
          "We work with selected partners across LATAM and EMEA to scale white-label financial infrastructure responsibly.",
        ],
      },
      press: {
        title: "Press",
        subtitle: "Resources for journalists, official communications and downloadable brand kit.",
        content: [
          "Press inquiries, official statements and executive briefings are coordinated through our communications team.",
          "Journalists covering financial infrastructure, embedded finance or B2B liquidity can request interviews with IBYLIT leadership.",
          "Brand assets — logos, color palette and product imagery — are available on request at soporte@ibylit.com.",
        ],
      },
      careers: {
        title: "Careers",
        subtitle: "Building the next generation of financial infrastructure. Open roles in product, engineering and go-to-market.",
        content: [
          "IBYLIT is hiring across product management, platform engineering, solutions architecture and enterprise go-to-market.",
          "We operate as a distributed team with a strong bias for ownership, technical depth and direct engagement with enterprise clients.",
          "If you want to shape the financial infrastructure layer that banks and corporates will run on, get in touch at soporte@ibylit.com.",
        ],
      },
    },
  },
  heroV2: {
    eyebrow: "Financial infrastructure and digital transformation",
    headline: "Financial infrastructure to transform how banks and companies operate",
    sub: "IBYLIT designs, implements, integrates and operates digital financial solutions on top of each organization's existing infrastructure.",
    cta: "Request Demo",
    ctaPrimary: "Explore our capabilities",
    ctaSecondary: "Request an assessment",
    counter: "01 / 09",
    badge: "LIVE",
    proofValidated: { label: "Ownership", value: "Confirmed" },
    proofSettlement: { label: "Settlement", value: "SINPE Direct" },
    orchestrator: {
      folioLabel: "FOLIO",
      folio: "CRC-882-9011",
      amountLabel: "Invoice face value",
      amount: "₡14,850,200",
      acceptedByPayer: "Accepted by payer",
      rows: {
        advance: { label: "Invoice advance", valuePrefix: "Discount rate", valueNumber: "1.15%", tag: "Settled T+0" },
        auction: { label: "Funding auction", valuePrefix: "", valueNumber: "4", valueSuffix: "active bids", tag: "Best rate" },
        registry: { label: "Book-entry registration", value: "GAUDI-9928-IBY", tag: "Active custody" },
      },
    },
  },
  productLive: {
    eyebrow: "Live product",
    title: "Real-time operational control — no tickets, no spreadsheets.",
    sub: "End-to-end visibility of every asset, every bid and every settlement — with auditable rules and traceability on demand.",
    browserUrl: "",
    chips: [
      { label: "Live states", desc: "Every operation, in real time" },
      { label: "Operational control", desc: "Auditable rules and flows" },
      { label: "End-to-end traceability", desc: "Origination → execution → settlement" },
    ],
    alt: "IBYLIT console — Live auctions",
  },
  problemV2: {
    eyebrow: "What we solve",
    title: "Liquidity is fragmented. The infrastructure to connect it doesn't exist.",
    intro: "Companies, banks, ERPs and funders operate in disconnected silos. The result: trapped capital, manual processes, no traceability.",
    items: [
      "Fragmentation between ERPs, banks and funders",
      "Manual processes without traceability",
      "Liquidity trapped in unstructured assets",
      "No neutral orchestrator",
    ],
  },
  productsShowcase: {
    eyebrow: "Products",
    title: "Multi-product. One infrastructure.",
    sub: "Each product structures a piece of the financial flow. Together they form the operational backbone, extensible to new instruments as the business evolves.",
    items: [
      {
        key: "factoring",
        name: "Factoring",
        solves: "Companies sell their accounts receivable to a third party (factor) to obtain immediate liquidity, transferring the collection rights of the credit. It can be structured with or without recourse, depending on who assumes the risk of non-payment.",
        how: ["Asset upload", "Validation & structuring", "Funder allocation"],
        diff: ["Verified assets", "Auction-ready", "Full traceability"],
        processing: "Documents are structured and validated before entering the negotiation flow.",
      },
      {
        key: "confirming",
        name: "Confirming",
        solves: "Corporations want to extend payment terms while their suppliers receive early payment.",
        how: ["Invoice approval", "Supplier offer", "Funder settlement"],
        diff: ["White-label", "Multi-funder", "Supply-chain ready"],
        processing: "Documents are structured and validated before entering the negotiation flow.",
      },
      {
        key: "payments",
        name: "B2B Payments",
        solves: "Inter-company payment flows lack visibility, structure and reconciliation.",
        how: ["Flow orchestration", "Bank rail routing", "Auto reconciliation"],
        diff: ["Bank-rail agnostic", "Traceable", "Embeddable"],
        processing: "Documents are structured and validated before being routed for execution.",
      },
      {
        key: "auctions",
        name: "Auctions",
        solves: "Asset price discovery is opaque. Funders compete without structured visibility.",
        how: ["Asset listing", "Competitive bidding", "Verifiable adjudication"],
        diff: ["Price discovery", "Verifiable", "Multi-funder"],
        processing: "Documents are structured and validated before entering the negotiation flow.",
      },
      {
        key: "annotation",
        name: "Annotation",
        solves: "Financial instruments need a unique digital identity throughout their lifecycle.",
        how: ["Digital registry", "Unique identifier", "Immutable metadata"],
        diff: ["Lifecycle-bound", "Auditable", "Interoperable"],
        processing: "Processed information feeds the operational registry and position traceability.",
      },
      {
        key: "custody",
        name: "Operational Custody",
        solves: "The instrument's state must be safeguarded across origination, transfer and settlement.",
        how: ["State management", "Event tracking", "Lifecycle closure"],
        diff: ["Operational only", "Internal registry", "Not regulated custody"],
        processing: "Processed information feeds the operational registry and position traceability.",
      },
    ],
    counter: "PRODUCT",
  },
  annotationDeep: {
    eyebrow: "Annotation & Operational Custody",
    title: "The full instrument lifecycle, registered and traced.",
    sub: "From origination to closure, every event is recorded. Internal registry — not regulated custody.",
    timeline: ["Origination", "Annotation", "Assignment", "Funding", "Settlement", "Closure"],
    blocks: [
      {
        title: "Annotation",
        body: "Digital registry of the instrument with unique identifier and operational metadata. Each instrument exists as a verifiable record across its lifecycle.",
      },
      {
        title: "Operational Custody",
        body: "Logical safekeeping of the instrument's state across origination, assignment, funding and settlement. Internal platform function, not regulated custody.",
      },
    ],
    disclaimer: "Internal registry — does not constitute regulated custody.",
  },
  ecosystemFlow: {
    eyebrow: "Connected ecosystem",
    title: "Every actor in the ecosystem.",
    sub: "Ibylit connects actors, governs the operation and runs multiple financial structures —factoring, confirming, auctions and payments— on a single infrastructure.",
    reinforcement: "Every actor operates under clear rules, with full visibility of the process and zero friction between systems.",
    hub: { name: "IBYLIT", role: "Neutral orchestrator", does: "Coordinates the flow between all participants under unified rules.", sees: "Complete state of every operation end to end." },
    inputs: { title: "What enters the flow", items: ["Assets from ERPs", "Approved invoices", "Available capital"] },
    outputs: { title: "What exits the flow", items: ["Structured liquidity", "Traceable settlement", "Reconciled flows"] },
    actors: [
      { key: "payer", name: "Payer", role: "Company paying obligations", does: "Originates debt and approves invoices for scheduled payment.", sees: "Payment status, suppliers and obligation calendar." },
      { key: "supplier", name: "Supplier", role: "Company collecting", does: "Assigns invoices for liquidity advance.", sees: "Available liquidity, collection dates and discount offers." },
      { key: "funder", name: "Funder", role: "Provides capital", does: "Selects opportunities and funds operations.", sees: "Active portfolio, returns and counterparty exposure." },
      { key: "bank", name: "Bank", role: "Provides rails and core", does: "Executes movements and bank reconciliation.", sees: "Transactional flows and reconciliation in real time." },
      { key: "factor", name: "Factor", role: "Structures discount", does: "Defines commercial conditions and operation rates.", sees: "Active portfolio, margins and aggregate risk." },
      { key: "settler", name: "Settler", role: "Executes closings", does: "Coordinates settlement and liquidation dates.", sees: "Operational calendar and closing status." },
      { key: "custodian", name: "Custodian", role: "Safeguards instruments", does: "Registers ownership and instrument movements.", sees: "Inventory, ownership and historical traceability." },
      { key: "erp", name: "ERP / Accounting system", role: "Syncs data", does: "Issues documents and syncs financial information.", sees: "Automated reconciliation and accounting reports." },
    ],
    detailLabels: { role: "Role", does: "What it does", sees: "What it sees" },
    nodes: { hub: "IBYLIT", banks: "Banks", erps: "ERPs", companies: "Companies", funders: "Funders" },
    processingChip: "Intelligent processing",
    processingTooltip: "Ibylit transforms commercial documents into structured data through automated extraction, normalization and validation, reducing manual errors and improving operational consistency.",
  },
  byActor: {
    eyebrow: "By Actor",
    title: "What each participant gets.",
    sub: "Same infrastructure. Different value depending on your role.",
    actors: [
      {
        key: "banks",
        name: "Banks",
        valueLine: "Launch financial products without replacing your core.",
        benefits: [
          "Factoring, confirming and auctions under your brand",
          "Non-invasive integration with your banking core",
          "Time-to-market reduced from years to months",
        ],
      },
      {
        key: "corporates",
        name: "Corporates",
        valueLine: "Structure supplier programs and optimize working capital.",
        benefits: [
          "Confirming programs end-to-end",
          "Point-to-point traceability of every flow",
          "Working capital optimization",
        ],
      },
      {
        key: "smes",
        name: "SMEs",
        valueLine: "Access liquidity on your invoices, without operational friction.",
        benefits: [
          "Connection to a network of verified funders",
          "Digital, transparent process",
          "No technology investment required",
        ],
      },
      {
        key: "funders",
        name: "Funders",
        valueLine: "Structured access to verified opportunities.",
        benefits: [
          "Verified opportunities, structured for analysis",
          "Verifiable auctions and traceable settlement",
          "Operational custody of allocated instruments",
        ],
        disclaimer: "Internal registry — does not constitute regulated custody.",
      },
    ],
    cta: "Talk to the team",
  },
  architectureLayers: {
    eyebrow: "Architecture",
    title: "Modular by design. Integrable by default.",
    sub: "Five layers that compose the infrastructure. Use them together or embed them individually.",
    layers: [
      { num: "01", title: "Cloud-Native APIs", body: "Direct integration via REST APIs and webhooks. No SDKs, no installations, no library maintenance." },
      { num: "02", title: "Flow Orchestration", body: "Coordinates the lifecycle between actors without executing financial operations." },
      { num: "03", title: "Annotation & Lifecycle", body: "Digital registry of instruments with full event traceability." },
      { num: "04", title: "Integrations", body: "Connectors for ERPs, banking cores, accounting and tax authorities." },
      { num: "05", title: "Security & Compliance", body: "TLS 1.3 in transit, encryption at rest, row-level access control and full audit trail. Designed against SOC 2 and ISO 27001 control frameworks." },
    ],
  },
  intelligence: {
    eyebrow: "Intelligence",
    body: "Applied intelligence on operational decisions — scoring, routing and prioritization. Augments human judgment, never replaces it.",
  },
  solutionOverview: {
    eyebrow: "WHAT IBYLIT IS",
    title: "We don't replace your core. We orchestrate it.",
    intro: "Ibylit is a cloud-native B2B white-label financial orchestration layer. It does not replace your core systems: it connects them, coordinates actors and moves liquidity flows in real time.",
    blocks: [
      { label: "What it is", body: "An orchestration layer between banks, corporates, funders and SMEs, on top of your existing infrastructure." },
      { label: "What it isn't", body: "Not a core banking system, not an ERP, not an isolated vertical SaaS." },
      { label: "How it positions", body: "Integrates data, actors and the full lifecycle: origination → decision → execution → settlement → custody." },
    ],
  },
  intelligenceLayer: {
    eyebrow: "PROCESSING LAYER",
    aiBadge: "Powered by AI",
    title: "Intelligent processing integrated into operations",
    intro: "The platform incorporates a processing layer, powered by AI, that converts unstructured information into operable, verifiable and traceable data within the financial flow.",
    mockup: {
      title: "Upload Invoices",
      stepActive: "Validate invoices",
      stepNext: "Register invoices",
      productLabel: "Select the product to continue",
      products: ["Confirming", "Supplier Payment", "Factoring", "Auctions"],
      template: "Download template",
      dropTitle: "Drag your invoices here",
      dropHint: "or click the button",
      dropCta: "Select invoices with AI",
      formats: "Accepted: .XML, .XLS, .XLSX, .PDF, .PNG, .JPG",
      validate: "Validate invoices",
      next: "Continue to step 2",
      caption: "Illustrative view — the actual screen may vary by deployment.",
    },
    flow: {
      label: "Operational flow",
      steps: ["Input", "Intelligent processing", "Structuring", "Execution", "Settlement"],
      highlight: 1,
    },
    capabilities: [
      { title: "Information structuring", body: "Transformation of documents into organized, usable data within the operational flow." },
      { title: "Consistency validation", body: "Verification of key fields and detection of inconsistencies before advancing in the process." },
      { title: "Reduced manual intervention", body: "Decrease of repetitive manual processes, reducing errors and operational friction." },
      { title: "Integration to the financial flow", body: "Structured data integrates directly into auction, allocation and settlement processes." },
    ],
    trust: "Processes are designed to produce consistent and verifiable results, prioritizing data quality and operational traceability.",
    disclaimer: "Document validation and automated processing support the quality and consistency of information, but do not replace legal or regulatory validations performed by the corresponding entities.",
    dashboardMockup: {
      title: "General dashboard",
      clientPlaceholder: "Client",
      caption: "Illustrative view. Demo data — does not represent real information.",
      kpis: {
        activeTotal: "Active total",
        overdueTotal: "Overdue total",
        invoicesCount: "Operations count",
        financedCollections: "Financed collections",
        unfinancedCollections: "Unfinanced collections",
        accumulatedReceivable: "Accumulated receivable",
      },
      projection: {
        title: "Monthly collection projection",
        today: "Due today",
        week: "Due this week",
        month: "Due this month",
        unitA: "Unit A",
        unitB: "Unit B",
        r1: "W1",
        r2: "W2",
        r3: "W3",
        r4: "W4",
        r5: "W5",
        r6: "W6",
      },
    },
  },
  finalCta: {
    eyebrow: "Get started",
    title: "Build your financial infrastructure with IBYLIT.",
    sub: "30 minutes. Your team and ours. No commitment.",
    cta: "Request Demo",
    contact: "soporte@ibylit.com",
  },
  demoForm: {
    eyebrow: "[ REQUEST DEMO ]",
    title: "Let's talk about your liquidity infrastructure.",
    subtitle: "30 minutes with our team. No commitment.",
    fields: {
      name: "Full name",
      email: "Corporate email",
      company: "Company",
      message: "Briefly tell us your use case (optional)",
    },
    placeholders: {
      name: "Jane Doe",
      email: "you@company.com",
      company: "Acme Inc.",
      message: "We're exploring factoring infrastructure for our marketplace…",
    },
    submit: "Send request",
    sending: "Sending…",
    success: "Got it. We'll get back to you within 24h.",
    error: "Something went wrong. Try again or write to soporte@ibylit.com.",
    rateLimited: "We already received a request from this email. For security reasons, please wait about 20 minutes before sending another, or email us directly at soporte@ibylit.com.",
    captchaRequired: "Please complete the verification before submitting.",
    consent: "By submitting you agree to be contacted by the IBYLIT team.",
    close: "Close",
  },
  fiscalInfra: {
    eyebrow: "FISCAL INTEGRATIONS",
    title: "Fiscal infrastructure connected across the region",
    subtitle: "Operating on official systems, in real time",
    description:
      "Ibylit connects directly with each country's fiscal systems to operate on official, validated and traceable information. The platform is already integrated with Costa Rica's Ministerio de Hacienda and Argentina's ARCA, building financial operations on a real regulatory foundation.",
    chips: ["Ministerio de Hacienda · Costa Rica", "ARCA · Argentina"],
    countries: [
      {
        key: "ar",
        code: "AR",
        name: "Argentina",
        subtitle: "Invoices as negotiable instruments",
        paragraph:
          "Ibylit operates on the Factura Electrónica de Crédito (FCE) regime, where every invoice follows a regulated flow.",
        items: [
          "Invoice status validation",
          "Acceptance or rejection confirmation",
          "Negotiable balance determination",
          "Market eligibility identification",
        ],
      },
      {
        key: "cr",
        code: "CR",
        name: "Costa Rica",
        subtitle: "Validation on electronic invoices",
        paragraph:
          "Ibylit checks the status of electronic invoices directly from Hacienda's system using their unique key.",
        items: [
          "Reception status verification",
          "Document existence confirmation",
          "Traceability from origin",
        ],
      },
    ],
    bridge: {
      title: "From validation to operation",
      intro:
        "Ibylit turns electronic documents into operable assets. Once validated, these documents can:",
      capabilities: [
        "Be annotated and kept traceable",
        "Be held in custody throughout their lifecycle",
        "Participate in regulated financial processes",
      ],
      processesLabel: "Enabled processes",
      processes: [
        "Factoring",
        "Confirming",
        "Liquidity auctions",
        "OTC operations",
        "Private buy & sell",
        "Ownership transfers",
      ],
    },
    closing:
      "Ibylit turns information from fiscal systems into a reliable foundation to annotate, hold in custody and trade financial assets across the region.",
  },
  regulatory: {
    eyebrow: "REGULATORY FRAMEWORK",
    title: "Infrastructure built on existing legal frameworks",
    intro:
      "Ibylit enables structuring digital liquidity operations on assets that already exist and operate under legal frameworks in force across multiple jurisdictions. The platform does not create new regulatory instruments nor replace financial intermediaries: it organizes and connects processes on top of existing legal structures.",
    indicators: [
      "Existing legal framework",
      "Operating assets",
      "Connective infrastructure",
    ],
    jurisdictions: [
      {
        key: "ar",
        name: "Argentina",
        intro: "Operations structured on assets under:",
        laws: [
          {
            code: "Ley 27.440",
            name: "Productive Financing · Electronic Credit Invoice (FCE)",
            flow: {
              label: "Enabled operational flow",
              intro: "Within this framework, Ibylit structures the full lifecycle of the Electronic Credit Invoice as a negotiable instrument:",
              items: [
                "Issuance of the Electronic Credit Invoice in compliance with current regulations",
                "Formal acceptance of the instrument by the payer (explicit or by expiration of the legal term)",
                "Registration of the accepted instrument with a collective deposit agent",
                "Structuring of financing through confirming, advances or regulated auctions",
                "Assignment and transfer of ownership within the regulated scheme",
                "Collection and settlement to the registered holder at maturity",
              ],
            },
          },
        ],
        note: "This framework enables the structured negotiation of the Electronic Credit Invoice as a financial instrument within regulated schemes.",
      },
      {
        key: "cr",
        name: "Costa Rica",
        intro: "Operations structured on assets framed by:",
        laws: [
          {
            code: "Ley 9691",
            name: "Electronic factoring and assignment of credit rights",
            flow: {
              label: "Enabled operational flow",
              intro: "On top of this framework, Ibylit structures the full credit lifecycle:",
              items: [
                "Validation of the electronic invoice before the Ministry of Finance",
                "Identification of the collection right as a financial asset",
                "Formalization of the assignment via factoring contracts",
                "Notification to the payer in accordance with current regulations",
                "Registration and traceability of the operation on an electronic platform",
                "Credit management by the factor",
              ],
            },
          },
          {
            code: "Ley 10039",
            name: "Executive nature of the electronic invoice and its potential book-entry registration as a negotiable security",
            flow: {
              label: "Extension to book-entry registration",
              intro: "When the model requires it, the flow can extend into securities-market schemes:",
              items: [
                "Registration request before a central securities depository",
                "Confirmation of payer acceptance (explicit or tacit within the legal term)",
                "Book-entry registration of the instrument as a security",
                "Custody and ownership registration",
                "Enablement for circulation and trading",
              ],
            },
          },
        ],
        note: "These frameworks enable the assignment, management and financing of receivables, as well as their structuring under book-entry and traceability schemes when securities-market infrastructures are used.",
      },
    ],
    role: {
      title: "Ibylit's role",
      intro: "Ibylit operates as an infrastructure layer that:",
      items: [
        "organizes information",
        "structures processes",
        "connects participants",
      ],
      closing: "without assuming the role of a regulated financial entity.",
    },
    riskReduction: {
      title: "Designed to reduce operational friction and perceived risk",
      items: [
        "Structuring on existing assets",
        "Complete operational traceability",
        "Verifiable evidence at every stage",
        "Integration with regulated actors",
        "Clear separation of roles",
        "Reduction of operational friction",
      ],
    },
    disclaimer:
      "Ibylit is not a financial entity, does not intermediate funds and does not act as a regulated custodian. The platform facilitates the structuring, traceability and operation of the full asset lifecycle — from validation through use in financing processes — on top of existing legal frameworks.",
  },
  caseStudies: {
    eyebrow: "Case studies",
    title: "Real infrastructure. Connected operations. Liquidity in motion.",
    subtitle:
      "Production deployments where Ibylit acts as the orchestration layer, integrating core banking, market participants and external systems into real-time financial flows.",
    quote: "We don't replace systems. We connect them to operate in real time.",
    cases: [
      {
        flag: "🇨🇷",
        label: "Bank 1 · Costa Rica",
        resolvedTitle: "What was solved",
        resolved:
          "Integration of the confirming ecosystem without replacing the core banking system.",
        connectedTitle: "What was connected",
        connected: [
          "Core banking (risk, accounting, operations)",
          "Local transfers",
          "Interoperability with other banks",
          "International connectivity",
        ],
        achievedTitle: "What was achieved",
        achieved:
          "Continuous flow between origination, evaluation and execution, with unified information in real time.",
      },
      {
        flag: "🇨🇷",
        label: "Bank 2 · Costa Rica",
        resolvedTitle: "What was solved",
        resolved:
          "Rollout of payments and supply-chain financing products, integrating internal operations and external participants into a unified digital flow.",
        implementedTitle: "What was implemented",
        implemented: [
          "Corporate payment solutions",
          "Supplier financing",
          "With- and without-recourse models",
          "Financial flow automation",
        ],
        connectedTitle: "What was connected",
        connected: [
          "Internal bank systems",
          "Integration with payer and supplier ERPs",
          "Payments and transfers infrastructure",
          "Interaction with other financial entities",
        ],
        achievedTitle: "What was achieved",
        achieved:
          "Automated end-to-end operations, connecting companies and the bank in a single flow of information and execution.",
      },
      {
        flag: "🇦🇷",
        label: "Distributor platform · Argentina",
        resolvedTitle: "What was solved",
        resolved:
          "Full build of an instrument-based liquidity infrastructure.",
        connectedTitle: "What was implemented",
        connected: [
          "Digital annotation",
          "Legal custody",
          "Primary and secondary market trading",
          "DVP settlement",
          "Multiple competing funders",
        ],
        achievedTitle: "What was achieved",
        achieved:
          "Digital ecosystem where liquidity is generated through competitive interaction between participants.",
      },
    ],
    capabilitiesTitle: "Common capabilities across all cases",
    capabilities: [
      "Integration with existing systems (no replacement)",
      "Orchestration of multiple participants",
      "Real-time financial flows",
      "Operational and accounting automation",
      "Complete end-to-end traceability",
      "Scalable cloud architecture",
    ],
    resultLabel: "Result",
    result:
      "Fragmented processes turned into connected, automated and scalable financial flows.",
    cta: "See how it works",
  },
  calculator: {
    eyebrow: "Smart Quoter",
    title: "Simulate your invoice advance",
    subtitle:
      "Find out how much you can receive today for your pending invoices with our factoring calculator",
    inputTitle: "Invoice details",
    inputSubtitle: "Enter the details to get a quote",
    amountLabel: "Invoice amount",
    daysLabel: "Days to maturity",
    daysHint: "Between 1 and 365 days",
    calculating: "Calculating with AI...",
    calculate: "Quote with AI",
    resultTitle: "Result",
    resultSubtitle: "Your personalized quote",
    rateApplied: "Applied rate",
    receiveToday: "Amount to receive today",
    advanceCost: "Advance cost",
    recalc: "Re-quote with new rate",
    requestDemo: "Request Demo",
    emptyTitle: "Enter the details",
    emptyHint: 'And press "Quote with AI" to see your result',
    disclaimer:
      "* This simulation is illustrative. Real rates depend on risk profile, payer and market conditions. Contact our team for a personalized quote.",
  },
  mockups: {
    auction: {
      path: "/ Invoices",
      kpis: { active: "Active", closed: "Closed", volume: "Volume" },
      live: "LIVE",
      amount: "Amount",
      bids: "Bids",
      status: {
        auction: "In auction",
        approved: "Approved",
        cancelled: "Cancelled",
      },
      showing: "Showing",
      of: "of",
      invoices: "invoices",
      hideCards: "Hide cards",
      payer: "PAYER",
      negotiableAmount: "Reference amount",
      operator: "Operator",
      days: "days",
      rate: "Rate",
      offer: "Offer",
      noOffers: "No offers",
      illustrative: "Illustrative data",
      hideDetails: "Hide details",
      seeOffers: "See offers",
      invoiceHeader: "INVOICE - AUCTIONS",
      term: "Term",
      dueDate: "Due date",
      acceptedRate: "Accepted rate",
      bestOffer: "Best offer",
      provider: "Provider",
      providerName: "Demo Operator",
    },
    cancellation: {
      title: "Cancellation",
      path: "/ Invoices in progress",
      donut: { active: "Active", overdue: "Overdue", recovery: "Recovery" },
      search: "Search by supplier or invoice…",
      filters: "Filters",
      export: "Export",
      cols: {
        invoice: "Invoice",
        supplier: "Supplier",
        payer: "Payer",
        amount: "Amount",
        status: "Status",
      },
      status: { active: "Active", overdue: "Overdue" },
    },
  },
  eyebrows: {
    ecosystem: "Ecosystem",
    summary: "Summary",
    security: "Security",
    platform: "The platform",
    products: "Products",
    howItWorks: "How it works",
    differentiator: "Differentiator",
  },
  architectureExtra: {
    platformLabel: "White-Label Platform",
    coreLabel: "Cloud Financial Core",
    coreDesc: "Factoring • Confirming • B2B Payments • Auctions",
  },
  governanceExtra: {
    message: "Technology designed to scale",
    messageHighlight: "with your business",
  },
  operationalFlow: {
    eyebrow: "OPERATIONAL FLOW",
    title: "End-to-end operational flow",
    intro: "From invoice ingestion to legal closure. Each step reflects real system behavior.",
    nav: { prev: "Previous", next: "Next", step: "Step" },
    steps: [
      {
        num: "01",
        title: "Ingestion",
        description: "Invoice ingestion from ERP, XML or manual upload.",
        subItems: ["ERP", "XML / UBL", "Manual upload"],
        visualKey: "entrada",
      },
      {
        num: "02",
        title: "Intelligent processing",
        description: "Automatic validation, normalization and enrichment of each document.",
        subItems: ["Validation", "Normalization", "Enrichment", "Traceability"],
        visualKey: "procesamiento",
        ctaLabel: "See processing detail ↓",
      },
      {
        num: "03",
        title: "Structuring",
        description: "Definition of financial conditions and approvals based on operation type.",
        subItems: ["Confirming", "Advances", "Approval rules"],
        visualKey: "estructuracion",
      },
      {
        num: "04",
        title: "Execution",
        description: "Execution of disbursements, DVP or fund settlement via banking or market integration.",
        subItems: ["Disbursements", "DVP", "Settlement"],
        visualKey: "ejecucion",
      },
      {
        num: "05",
        title: "Settlement",
        subtitle: "Operational and legal closure",
        description: "Final registration with legal documentation, positions and accounting reconciliation.",
        subItems: ["Documentation", "Positions", "Balances", "Accounting entry", "Legal evidence"],
        visualKey: "settlement",
      },
    ],
    visuals: {
      panelLabels: {
        entrada: "app.ibylit.com / upload-invoices",
        procesamiento: "engine / processing",
        estructuracion: "operation / structure",
        ejecucion: "execution / TX-2847",
        settlement: "ledger / operational close",
      },
      entrada: {
        dropHint: "Drop invoices or connect your ERP",
        formats: "XML · UBL · CSV · PDF",
        fileProgress: "FAC-2847.xml · 67%",
        ready: "3 invoices received · ready to validate",
      },
      procesamiento: {
        rows: [
          "Structural validation",
          "Field normalization",
          "Enrichment (TaxID, score, sector)",
          "Traceability and custody",
        ],
        states: { ok: "OK", run: "IN PROGRESS", wait: "QUEUED" },
      },
      estructuracion: {
        tabs: ["Confirming", "Advances", "Approvals"],
        plazo: "Term",
        plazoValue: "30 / 60 / 90 days",
        tasa: "Annual rate",
        tasaValue: "8.5%",
        flowLabel: "Approval flow",
        nodes: ["Originator", "Committee L1", "Treasury L2"],
      },
      ejecucion: {
        nodes: ["Originator", "IBYLIT", "Bank"],
        monto: "Amount",
        tipo: "Type",
        estado: "Status",
      },
      settlement: {
        asiento: "Journal entry",
        rows: [
          { k: "Position", v: "+ 48,200" },
          { k: "Balance", v: "1,247,800" },
          { k: "Account", v: "1.1.05.001" },
        ],
        stamp: "✓ RECORDED · AUDITABLE",
      },
    },
  },
  common: {
    disclaimer: "Disclaimer",
  },
  ctaSection: {
    titleA: "Launch your digital liquidity platform.",
    titleB: "Under your own brand.",
    button: "Request Demo",
    footnote: "Contact us to learn how Ibylit can power your business",
  },
  heroSectionLegacy: {
    headlineLine1: "B2B factoring and confirming platform",
    headlineLine2: "White-Label",
    subheadline: "We empower financial institutions to offer digital liquidity solutions under their own brand.",
    cta: "Request demo",
  },
  problemSegments: {
    badge: "Who is it for?",
    title: "Designed for institutions seeking to scale",
    subtitle: "Ibylit enables financial institutions and large corporations to offer digital liquidity solutions under their own brand",
    items: [
      { title: "Banks", description: "Offer digital liquidity products to your corporate clients under your own brand." },
      { title: "Stock Exchanges", description: "Enable a marketplace for negotiating invoices and commercial documents." },
      { title: "Brokers & Fintechs", description: "Launch factoring and confirming products without building technology from scratch." },
      { title: "Corporations", description: "Manage supplier financing across your value chain efficiently." },
    ],
  },
  architectureActors: {
    title: "An ecosystem connecting all participants",
    closing: "Ibylit connects suppliers, payers and funders on a unified platform for digital liquidity management.",
    items: [
      { title: "Suppliers", description: "Access to invoice financing • Document self-management • Payment visibility" },
      { title: "Payers / Corporations", description: "Accounts payable management • Confirming programs • Working capital optimization" },
      { title: "Funders / Investors", description: "Marketplace access • Invoice purchasing • Investment dashboard" },
    ],
    securityFeatures: [
      "Advanced encryption",
      "Access control",
      "Full traceability",
      "Multi-user and multi-profile",
      "Scalable architecture",
    ],
  },
  solutionCapabilities: {
    title: "Technology built to scale",
    subtitle: "Cloud-native architecture with the capabilities financial institutions need",
    items: [
      { title: "Full white-label adaptation", description: "Customization that drives brand loyalty and customer lifetime value." },
      { title: "Cloud product core", description: "Multi-product, multi-currency and multi-country." },
      { title: "Security", description: "Cloud security with advanced encryption, operational resilience and scalable microservices architecture." },
      { title: "Marketplace per product", description: "A meeting space for negotiating commercial and financial documents." },
      { title: "Self-service", description: "Self-service suite for suppliers, payers, investors and financial institutions, multi-user and multi-profile." },
      { title: "API integration", description: "Interconnection with your internal systems to scale processes and automate tasks." },
      { title: "Notifications", description: "Real-time alerts to keep all ecosystem participants informed." },
      { title: "Data dashboard", description: "Real-time visualization and analytics for decision-making." },
    ],
  },
  useCaseProducts: {
    title: "Cloud financial products",
    subtitle: "Multi-product core to integrally manage digital liquidity operations",
    items: [
      { title: "Reverse Factoring / Confirming", description: "Cloud platform for managing accounts payable and supplier financing across value chains." },
      { title: "Digital Factoring", description: "Cloud platform that simplifies receivables financing operations by connecting suppliers, payers and financial entities." },
      { title: "B2B Payments", description: "Cloud platform that optimizes terms and management of electronic invoice payments across supply chains." },
      { title: "Electronic Auctions", description: "Cloud platform where suppliers, payers and funders access a 100% online marketplace for negotiating invoices and documents." },
    ],
  },
  executiveDeck: {
    title: "Why Ibylit?",
    prev: "Previous",
    next: "Next",
    slides: [
      {
        badge: "White-Label",
        title: "Complete financial suite, under your own brand",
        bullets: [
          "Factoring, Confirming, B2B Payments and Auctions",
          "Multi-product, multi-currency, multi-country",
          "Integrated front, middle and back office",
          "Full brand customization",
        ],
        message: "Launch digital liquidity products without building technology from scratch.",
      },
      {
        badge: "Technology",
        title: "Enterprise-grade cloud-native architecture",
        bullets: [
          "Advanced data encryption",
          "Operational resilience",
          "Scalable microservices",
          "API integration",
          "Multi-user and multi-profile",
        ],
        message: "Security and scalability for financial institutions.",
      },
      {
        badge: "Ecosystem",
        title: "Connects suppliers, payers and funders",
        bullets: [],
        message: "A marketplace for negotiating commercial and financial documents.",
      },
    ],
  },
  cookieConsent: {
    title: "We value your privacy",
    description:
      "We use essential cookies to operate this site (theme, language, anti-bot protection). With your consent, we may also use analytics and marketing cookies to improve our service. You can change your choice anytime.",
    cookiePolicy: "Cookie Policy",
    privacyPolicy: "Privacy Policy",
    actions: {
      accept: "Accept all",
      reject: "Reject non-essential",
      configure: "Configure",
      save: "Save preferences",
      preferences: "Cookie preferences",
    },
    categories: {
      essential: {
        title: "Essential",
        desc: "Required for security, language, theme and anti-bot protection (Cloudflare Turnstile). Always active.",
      },
      analytics: {
        title: "Analytics",
        desc: "Category prepared to understand site usage. No tracker is currently loaded. If one is added in the future, it will only run after your explicit consent through a controlled loader (loadWhenConsented).",
      },
      marketing: {
        title: "Marketing",
        desc: "Category prepared to measure campaigns or personalize content. No tracker is currently loaded. If one is added in the future, it will only run after your explicit consent through a controlled loader (loadWhenConsented).",
      },
    },
  },
  legal: {
    eyebrow: "Legal",
    lastUpdated: "Last updated",
    contactNotice:
      "For any question about this document or to exercise your rights of access, rectification, deletion, opposition or portability, please contact us:",
    privacy: {
      title: "Privacy Policy",
      updated: "April 30, 2026",
      intro:
        "IBYLIT is committed to protecting your personal data in accordance with the EU General Data Protection Regulation (GDPR – Regulation (EU) 2016/679) and Costa Rica's Law for the Protection of Personal Data (Law No. 8968). This Policy describes what data we collect, why, and your rights.",
      sections: [
        {
          heading: "1. Data controller",
          body: [
            "IBYLIT operates the website ibylit.com and the related platform. For matters related to personal data, the data controller can be reached at soporte@ibylit.com.",
          ],
        },
        {
          heading: "2. Data we collect",
          body: [
            "Demo requests: name, corporate email, company and message you submit through our forms.",
            "Operational metadata: hashed IP address, user agent and submission timestamp, used exclusively for fraud prevention and rate limiting.",
            "Local preferences: language and theme stored in your browser via localStorage. We do not transmit this information to our servers.",
          ],
        },
        {
          heading: "3. Purposes and legal basis",
          body: [
            "Replying to your demo request and follow-up commercial communication (legitimate interest / consent).",
            "Protecting the service against abuse, automated attacks and fraud (legitimate interest, GDPR art. 6.1.f).",
            "Complying with applicable legal and contractual obligations.",
          ],
        },
        {
          heading: "4. Data sharing and processors",
          body: [
            "We share data only with processors strictly necessary to operate the service: cloud infrastructure (AWS), backend platform, transactional email provider and Cloudflare Turnstile for anti-bot protection. All providers operate under appropriate data protection agreements.",
            "We do not sell personal data and we do not transfer it to third parties for their own marketing purposes.",
          ],
        },
        {
          heading: "5. International transfers",
          body: [
            "Some of our processors are located outside Costa Rica or the European Economic Area. When that happens, transfers are protected by Standard Contractual Clauses or equivalent safeguards.",
          ],
        },
        {
          heading: "6. Retention",
          body: [
            "Demo request data is kept for the time strictly necessary to handle your request and, where applicable, the business relationship. Operational logs are retained for limited periods aligned with security and legal requirements.",
          ],
        },
        {
          heading: "7. Your rights",
          body: [
            "You may exercise your rights of access, rectification, deletion, opposition, restriction of processing and data portability by writing to soporte@ibylit.com.",
            "Under GDPR, you also have the right to lodge a complaint with the competent supervisory authority. In Costa Rica, the supervisory authority is PRODHAB (Agencia de Protección de Datos de los Habitantes).",
          ],
        },
        {
          heading: "8. Security",
          body: [
            "We apply industry-standard technical and organizational measures: TLS 1.2/1.3 in transit, access controls, monitoring, rate limiting and bot protection. No system is fully infallible, but we work continuously to mitigate risks.",
          ],
        },
        {
          heading: "9. Role of the platform",
          body: [
            "IBYLIT acts exclusively as a technology infrastructure provider for the management, structuring and execution of financial operations between third parties.",
            "IBYLIT does not act as a financial institution, custodian, securities intermediary, or holder of credit rights, unless expressly stated in specific contractual agreements.",
            "The platform facilitates the connection between participants (companies, financial institutions, funders and other ecosystem actors), enabling the traceability, transmission and management of information related to operations such as factoring, confirming, B2B payments and the negotiation of financial instruments, without assuming ownership or direct control of the underlying funds or rights.",
          ],
        },
        {
          heading: "10. Changes",
          body: [
            "We may update this Policy. The version in effect is always the one published on this page, with the corresponding update date.",
          ],
        },
      ],
    },
    cookies: {
      title: "Cookie Policy",
      updated: "April 24, 2026",
      intro:
        "This page explains what cookies and similar technologies we use on ibylit.com, what they are for, and how you can manage them, in compliance with the EU ePrivacy Directive, GDPR and Costa Rica's Law No. 8968.",
      sections: [
        {
          heading: "1. What are cookies",
          body: [
            "Cookies are small files stored in your browser when you visit a website. They allow the site to remember information about your visit, such as language preferences. We also use equivalent technologies such as localStorage.",
          ],
        },
        {
          heading: "2. Cookies and storage we use today",
          body: [
            "Essential — language preference (ibylit-lang), theme preference (theme), defaults version (ibylit-defaults-version) and the consent record itself (ibylit-cookie-consent). All stored locally in your browser.",
            "Security — Cloudflare Turnstile cookies that protect our forms against automated abuse. These are strictly necessary and exempt from consent under the ePrivacy Directive.",
            "Analytics — category prepared in our architecture, with no active scripts at this time.",
            "Marketing — category prepared in our architecture, with no active scripts at this time.",
          ],
        },
        {
          heading: "3. Consent technical architecture",
          body: [
            "We have implemented a generic script loader (loadWhenConsented) that serves as the only technical pathway to initialize any non-essential dependency. No analytics, marketing, advertising pixel, A/B testing tool or third-party widget can load without going through this loader.",
            "The loader validates hasConsent(category) before injecting the script and subscribes to the consent-updated event to react in real time when you change your preferences. This guarantees that rejecting a category prevents the resource from running, not just from being displayed.",
          ],
        },
        {
          heading: "3. Managing your preferences",
          body: [
            "You can accept, reject or configure non-essential cookies through our consent banner the first time you visit. You can also clear your preferences at any time from your browser's storage settings, which will trigger the banner to appear again on your next visit.",
            "Most browsers allow you to block or delete cookies from their settings. Please note that disabling essential cookies may affect the functioning of the site.",
          ],
        },
        {
          heading: "4. Updates",
          body: [
            "If we add new technologies or third-party services, we will update this page and, where required, request your consent again.",
          ],
        },
      ],
    },
    terms: {
      title: "Terms of Use",
      updated: "April 24, 2026",
      intro:
        "By accessing ibylit.com you agree to these Terms of Use. If you do not agree, please refrain from using the site.",
      sections: [
        {
          heading: "1. Purpose of the site",
          body: [
            "ibylit.com is an institutional and commercial site that presents IBYLIT's financial orchestration platform. It does not, by itself, provide financial services to end users; access to the platform is granted under separate contractual agreements.",
          ],
        },
        {
          heading: "2. Permitted use",
          body: [
            "You agree to use the site lawfully, without interfering with its operation, attempting unauthorized access, sending malicious code or automated requests beyond reasonable use.",
          ],
        },
        {
          heading: "3. Intellectual property",
          body: [
            "The brand IBYLIT, logos, content, design and software of the site are protected by intellectual property rights. Any reproduction, distribution or derivative use without prior written consent is prohibited.",
          ],
        },
        {
          heading: "4. Disclaimers",
          body: [
            "Information published on the site is provided for general information purposes. It does not constitute legal, tax, accounting or financial advice. We strive to keep it accurate and up to date, but we do not guarantee the absence of errors or service interruptions.",
          ],
        },
        {
          heading: "5. Limitation of liability",
          body: [
            "To the maximum extent permitted by law, IBYLIT shall not be liable for indirect, incidental or consequential damages arising from the use of the site.",
          ],
        },
        {
          heading: "6. External links",
          body: [
            "The site may contain links to third-party sites. We are not responsible for the content or practices of those sites.",
          ],
        },
        {
          heading: "7. Applicable law and jurisdiction",
          body: [
            "These Terms are governed by the laws of the Republic of Costa Rica. Any dispute arising from access to or use of the site shall be submitted to the competent courts of Costa Rica, without prejudice to the mandatory rights of consumers and data subjects under the GDPR for users located in the European Union.",
          ],
        },
        {
          heading: "8. Changes",
          body: [
            "We reserve the right to update these Terms. Continued use of the site after changes are published implies acceptance of the new version.",
          ],
        },
      ],
    },
  },
};
